import { error } from '@pressreader/logger';

import { readIdentity } from './identity.snapshot';

/**
 * `status` of the error a fenced query settles with when the identity changed while it was in flight.
 * Shaped like an RTKQ base-query error (`{ status, ... }`) so consumers can discriminate on `status`.
 */
export const IDENTITY_CHANGED = 'IDENTITY_CHANGED' as const;

/**
 * The one standardized error every identity-crossing settle produces — plain data (no methods, safe to
 * freeze: RTK Query writes it into the cache entry, where immer deep-freezes it in dev).
 */
export interface IdentityChangedError {
    status: typeof IDENTITY_CHANGED;
    /** Identity key in force when the query STARTED. */
    previousKey: string;
    /** Identity key in force when it SETTLED. */
    currentKey: string;
}

/**
 * The SECOND shape the same event arrives in: the commit-time reconcile aborting a request that was in flight on
 * a key it is repairing — any non-`invariant` class, since the abort is how a busy key is freed — with
 * `IDENTITY_CHANGED` as the abort reason (RTKQ reduces an aborted thunk into
 * `{ name: 'AbortError', message: <reason> }`). An abort is the one identity-crossing outcome the fence cannot
 * convert, because it discards the settle before the fence can see it.
 *
 * It carries no keys — nothing about the previous identity's request was read. They are declared as `undefined`
 * so the union stays directly readable (`error.previousKey` compiles for either shape).
 */
export interface IdentityChangedAbort {
    name: 'AbortError';
    message: typeof IDENTITY_CHANGED;
    previousKey?: undefined;
    currentKey?: undefined;
}

/**
 * Narrow an unknown rejection (`entry.error`, an `unwrap()` catch) to an identity change — either shape it
 * arrives in: a settle the fence converted, or the reconcile's own identity-reasoned abort of a request it
 * displaced (see `IdentityChangedAbort`). Both are the same consumer event, so both narrow here rather than
 * making every call site check twice.
 *
 * Consumers should treat it as "still loading, retry after the commit" — NOT as a failure: the commit-time
 * reconcile is what repopulates the entry, so surfacing it as an error produces a flash where the previous
 * behavior showed a spinner.
 *
 * A bare `AbortError` — an unmounted hook, RTKQ's own cache collection, a consumer's own `abort()` — is NOT one
 * of these: only the reason the reconcile names is recognized.
 *
 * Both arms are matched STRICTLY, because this guard is what stands between an identity outcome and a real
 * failure's own handling (it is the discriminant behind `QueryReadResult`, and a false positive there
 * suppresses the caller's error path silently). The settle arm therefore requires the two keys the fence always
 * emits below, not merely the `status` sentinel, so a malformed or colliding `{ status: 'IDENTITY_CHANGED' }`
 * from anywhere else stays a plain failure; the abort arm is literal-exact for the same reason.
 */
export function isIdentityChangedError(value: unknown): value is IdentityChangedError | IdentityChangedAbort {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const { status, name, message, previousKey, currentKey } = value as {
        status?: unknown;
        name?: unknown;
        message?: unknown;
        previousKey?: unknown;
        currentKey?: unknown;
    };
    if (status === IDENTITY_CHANGED) {
        return typeof previousKey === 'string' && typeof currentKey === 'string';
    }
    return name === 'AbortError' && message === IDENTITY_CHANGED;
}

/** The token identity `identityScoped` hands to the queryFn it wraps, on the RTKQ `api` object. */
export interface QueryIdentity {
    /** Normalized identity key of the token in force when the query started (see `IdentityClock.key`). */
    readonly key: string;
    /** `false` when that key is the anonymous key — the boolean an endpoint's auth gate wants. */
    readonly isAuthenticated: boolean;
}

/**
 * The RTK Query `queryFn` api object as a FENCED queryFn sees it: RTKQ's own fields plus `identity`.
 *
 * Annotate the wrapped queryFn's second parameter with this type when composing with another combinator,
 * so the identity survives the composition:
 *
 * @example
 * queryFn: identityScoped(
 *     yieldOnRace(async (_arg: void, { getState, identity }: IdentityQueryApi) => {
 *         if (!identity.isAuthenticated) {
 *             return { data: [] };
 *         }
 *         ...
 *     }),
 * ),
 */
export interface IdentityQueryApi {
    getState(): unknown;
    /** RTKQ passes this for queries; it keys this query's cache entry. */
    queryCacheKey?: string;
    signal?: AbortSignal;
    identity: QueryIdentity;
}

/** Marks a queryFn as fenced, so `declareIdentityPolicy` can reject an endpoint that forgot the wrapper. */
const IDENTITY_SCOPED_MARKER = '__prIdentityScoped';

type IdentityScopedMarker = { [Key in typeof IDENTITY_SCOPED_MARKER]?: boolean };

/** True for a queryFn produced by `identityScoped` (the marker survives because it is the OUTERMOST wrapper). */
export function isIdentityScoped(queryFn: unknown): boolean {
    return (queryFn as IdentityScopedMarker | null | undefined)?.[IDENTITY_SCOPED_MARKER] === true;
}

type QueryFnResult = { data: unknown } | { error: unknown };

/**
 * The write fence itself: a settled outcome is let through only while the identity generation still matches the
 * one the call started under, and becomes the standardized error otherwise. Shared by the resolve and the throw
 * path, so both settle by exactly the same rule.
 */
function settleUnderIdentity<Result>(started: ReturnType<typeof readIdentity>, result: Result): Result | { error: IdentityChangedError } {
    const settled = readIdentity();
    if (settled.generation !== started.generation) {
        return { error: { status: IDENTITY_CHANGED, previousKey: started.key, currentKey: settled.key } };
    }
    return result;
}

/**
 * Fence a query's `queryFn` to the identity that started it.
 *
 * Two jobs, both aimed at the same bug class — a request started under identity A writing its result into a
 * cache that now belongs to identity B:
 *
 * 1. **Gate input**: the wrapped queryFn receives the TOKEN identity (`api.identity`) instead of reading the
 *    Redux auth slice, which lags the token by 1–3 round trips (it only catches up at the login/logout
 *    commit). A gate on the slice returns empty data for a request that is already authenticated.
 * 2. **Write fence**: the identity GENERATION is snapshotted at start and re-checked at settle; if it moved,
 *    the settle is converted into the standardized `IDENTITY_CHANGED` error instead of being written as data.
 *    Generation (not key) equality closes A -> B -> A.
 *
 * It RETURNS `{ error }` and never throws, and that distinction is load-bearing for the standing repair
 * listener: returning `{ error }` produces a `rejectedWithValue` action carrying the error as its PAYLOAD, which
 * is exactly what `registerIdentityRejectionRepair` matches on, while a thrown error reaches RTKQ as a plain
 * `rejected` action whose payload is empty — invisible to that predicate, so an entry left resting on it would
 * never be repaired. A wrapped queryFn that THROWS is therefore converted to the same `{ error }` form (and
 * logged), so the `rejectedWithValue` shape and the identity conversion hold for a throw exactly as for a
 * returned error.
 *
 * Compose it OUTERMOST — `identityScoped(yieldOnRace(fn))` — so the fence sees the final settle and the
 * marker lands on the queryFn the endpoint actually installs.
 *
 * The residual it does not close: the microtask between this final check and RTKQ's `fulfilled` dispatch.
 * The commit-time reconcile (see `declareIdentityPolicy`) is what heals that.
 *
 * @example
 * getItems: build.query<Item[], void>({
 *     queryFn: identityScoped(async (_arg, { identity }) => {
 *         if (!identity.isAuthenticated) {
 *             return { data: [] };
 *         }
 *         return { data: (await fetchItems()).map(mapToItem) };
 *     }),
 *     providesTags: [TAG_ITEMS],
 * }),
 */
export function identityScoped<Arg, Api extends IdentityQueryApi, Result extends QueryFnResult>(
    queryFn: (arg: Arg, api: Api, extraOptions?: unknown, baseQuery?: unknown) => Promise<Result>,
    // `{ error: unknown }` covers both settle-time errors this can produce: the standardized
    // `IdentityChangedError` and a caught throw, re-emitted as a value.
): (arg: Arg, api: Omit<Api, 'identity'>, extraOptions?: unknown, baseQuery?: unknown) => Promise<Result | { error: unknown }> {
    const fenced = async (arg: Arg, api: Omit<Api, 'identity'>, extraOptions?: unknown, baseQuery?: unknown) => {
        const started = readIdentity();
        const identity: QueryIdentity = { key: started.key, isAuthenticated: started.isAuthenticated };
        try {
            // The identity rides on the api object because that is the only channel that survives an inner
            // combinator (`yieldOnRace` forwards `api` untouched). Cast: TS cannot know the reconstructed object
            // is exactly `Api` again after `Omit`.
            return settleUnderIdentity(started, await queryFn(arg, { ...api, identity } as Api, extraOptions, baseQuery));
        } catch (e) {
            // Left to propagate, this would reach RTKQ as a plain `rejected` action: no `providesTags` recorded
            // (so the commit reconcile could never force this entry to refetch) and no identity conversion.
            error('A fenced queryFn threw; rejecting with the error as a value so the entry keeps its tags.', e);
            return settleUnderIdentity(started, { error: e });
        }
    };
    return Object.assign(fenced, { [IDENTITY_SCOPED_MARKER]: true });
}
